import { Add, AddCircle } from '@mui/icons-material'
import { CircularProgress } from '@mui/material'
import { T, useTranslate } from '@tolgee/react'
import axios from 'axios'
import classNames from 'classnames'
import { useWindowDimensions } from 'components/hooks/window'
import { ViewContext } from 'components/lib'
import { errorToast, successToast } from 'helpers'
import { saveToGallery } from 'helpers/image'
import moment from 'moment'
import { useContext, useEffect, useState } from 'react'
import { DEFAULT_MODAL_DATA, Modal } from 'toolComponents/generic/modal'
import { Input, ListPosition, Microbutton, ReactSelect } from 'ui'
import GalleryList from './GalleryList'

export default function GalleryLayout({
  galleries,
  refreshGalleries,
  selectedGallery,
  setSelectedGallery,
  galleryItems,
  gallerySearch,
  setGallerySearch,
  setGalleryItems,
  fetchItems,
}) {
  const { t } = useTranslate()
  const { width } = useWindowDimensions()
  const context = useContext(ViewContext)
  const [modalData, setModalData] = useState(DEFAULT_MODAL_DATA)

  const [isUploading, setIsUploading] = useState(false)
  const [isShowSelector, setIsShowSelector] = useState(true)

  const [newName, setNewName] = useState('')
  const [newGallery, setNewGallery] = useState()

  useEffect(() => {
    setNewGallery()
  }, [selectedGallery, galleries.data])

  const handleCreateGallery = async (name) => {
    if (!name?.length) return

    try {
      const res = await axios.post('/api/gallery/create', { name })
      refreshGalleries()

      setSelectedGallery(res.data)
    } catch (err) {
      context.handleError(err)
    }
  }

  const handleRenameGallery = async (id, newName) => {
    try {
      await axios.post('/api/gallery/rename', { id, newName })
      refreshGalleries()
      setSelectedGallery((prev) => ({ ...prev, label: newName }))
    } catch (err) {
      context.handleError(err)
    }
  }

  const handleRenameImage = async (url, newName) => {
    try {
      await axios.post('/api/gallery/rename-item', {
        url: url,
        newName: newName,
        galleryId: selectedGallery.value,
      })
      await fetchItems()
      successToast(<T keyName='eleo-rename-image-success'>Image renamed successfully</T>)
    } catch (err) {
      errorToast(<T keyName='eleo-rename-image-failed'>{err.response.data.message}</T>)
    }
  }

  const handleChangeDescription = async (url, newDescription) => {
    try {
      await axios.post('/api/gallery/change-item-description', {
        url: url,
        newDescription: newDescription,
        galleryId: selectedGallery.value,
      })
      await fetchItems()
      successToast(<T keyName='eleo-change-description-success'>Description changed</T>)
    } catch (err) {
      errorToast(<T keyName='eleo-rename-image-failed'>{err.response.data.message}</T>)
    }
  }

  const handleMoveImage = async (url, toGalleryId) => {
    try {
      await axios.post('/api/gallery/move-item', {
        url: url,
        fromGalleryId: selectedGallery.value,
        isFromMediaGallery: selectedGallery.isMediaGallery,
        toGalleryId: toGalleryId,
      })
      await fetchItems()
      successToast(<T keyName='eleo-move-image-success'>Image moved successfully</T>)
    } catch (err) {
      errorToast(<T keyName='eleo-move-image-failed'>{err.response.data.message}</T>)
    }
  }

  const handleDeleteGallery = async (id) => {
    try {
      const toDelete = galleries.data.find((gallery) => gallery.value === id)
      if (!toDelete?.label)
        return errorToast(
          <T keyName='eleo-error-delete-default-gallery'>Cannot delete the default gallery</T>
        )

      await axios.delete(`/api/gallery/${id}`)
      await refreshGalleries()
      setSelectedGallery(
        galleries.data.find((gallery) => gallery.value !== id && !gallery.isMediaGallery)
      )
    } catch (err) {
      context.handleError(err)
    }
  }

  async function handleFileUpload(e, isImage = false) {
    e.preventDefault()

    const files = e.target.files
    if (!files.length) return

    setIsUploading(true)

    try {
      await Promise.all(
        Array.from(files).map(async (file) => {
          const url = URL.createObjectURL(file)
          await saveToGallery(
            url,
            file.name ?? t('eleo-uploaded-image-temp-name', 'Uploaded image'),
            null,
            selectedGallery?.value
          )
        })
      )
    } catch (err) {
      console.log(err)
      context.handleError(err)
    } finally {
      // Clear file input
      e.target.value = null
      setIsUploading(false)
      setModalData(DEFAULT_MODAL_DATA)
      await fetchItems()
    }
  }

  let filteredItems = galleryItems.data.filter(
    (item) =>
      item.name?.toLowerCase().includes(gallerySearch.toLowerCase()) ||
      item.description?.toLowerCase().includes(gallerySearch.toLowerCase()) ||
      moment(item.date_created).format('DD.MM.YYYY h:mm').includes(gallerySearch)
  )
  filteredItems = filteredItems.sort((a, b) => (a.date_created < b.date_created ? 1 : -1))

  const modalContent = {
    create: (
      <div className='space-y-[5px] p-[24px]'>
        <p className='body-small text-brand-gray-dark'>
          {t('eleo-your-story-modal-create-input-label', 'Name')}
        </p>
        <input
          autoFocus
          spellCheck={false}
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          className='h-[36px] w-full rounded-[4px] border bg-white py-[10px] pl-3 pr-[4px] text-[13px]'
          placeholder=''
        />
      </div>
    ),
    rename: (
      <div className='space-y-[5px] p-[24px]'>
        <p className='body-small text-brand-gray-dark'>
          {t('eleo-gallery-modal-create-input-label', 'Name')}
        </p>
        <input
          autoFocus
          spellCheck={false}
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          className='h-[36px] w-full rounded-[4px] border bg-white py-[10px] pl-3 pr-[4px] text-[13px]'
          placeholder=''
        />
      </div>
    ),
    'rename-image': (
      <div className='space-y-[5px] p-[24px]'>
        <p className='body-small text-brand-gray-dark'>
          {t('eleo-gallery-modal-create-input-label', 'Name')}
        </p>
        <input
          autoFocus
          spellCheck={false}
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          className='h-[36px] w-full rounded-[4px] border bg-white py-[10px] pl-3 pr-[4px] text-[13px]'
          placeholder=''
        />
      </div>
    ),
    'change-description': (
      <div className='space-y-[5px] p-[24px]'>
        <p className='body-small text-brand-gray-dark'>
          {t('eleo-gallery-modal-change-description-input-label', 'Description')}
        </p>
        <textarea
          autoFocus
          spellCheck={false}
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          style={{ fieldSizing: 'content' }}
          className='max-h-40 min-h-24 w-full rounded-[4px] border bg-white py-[10px] pl-3 pr-[4px] text-[13px]'
          placeholder=''
        />
      </div>
    ),
    'move-image': (
      <div className='space-y-[5px] p-[24px]'>
        <p className='body-small text-brand-gray-dark'>
          <T
            keyName='eleo-modal-save-to-gallery-gallery-selection-label'
            defaultValue='Choose gallery'
          />
        </p>
        <ReactSelect
          options={galleries.data.filter((gallery) => !gallery.isMediaGallery)}
          setSelectedValue={({ value, label }) => setNewGallery({ value, label })}
          value={newGallery}
          isClearable={false}
        />
      </div>
    ),
  }

  const handleCallback = async (type, context, isSubmitSecondaryEffect) => {
    switch (type) {
      case 'create':
        handleCreateGallery(newName)

        setNewName('')

        break
      case 'rename':
        handleRenameGallery(selectedGallery.value, newName)

        setNewName('')

        break

      case 'rename-image':
        handleRenameImage(context.imageUrl, newName)

        setNewName('')

        break

      case 'change-description':
        handleChangeDescription(context.imageUrl, newName)

        setNewName('')

        break

      case 'move-image':
        handleMoveImage(context.imageUrl, newGallery.value)

        setNewGallery()

        break

      case 'delete':
        handleDeleteGallery(selectedGallery.value)

        break

      default:
        break
    }

    setIsShowSelector(false)
  }

  return (
    <div className='h-full overflow-clip'>
      <div className='flex flex-wrap items-center justify-between border-b px-4 py-3 text-lg'>
        <div>
          <T keyName='eleo-tool-gallery-title'>GALLERY</T>
        </div>
        <div className='flex gap-4'>
          {!selectedGallery?.isMediaGallery && (
            <>
              <label htmlFor='image-input'>
                <Microbutton
                  isLoading={isUploading}
                  text={<T keyName='eleo-galery-upload-image'>Upload new image</T>}
                />
              </label>
              <input
                id='image-input'
                type='file'
                className='hidden'
                multiple={true}
                accept={['image/jpeg', 'image/png'].join(',')}
                onChange={handleFileUpload}
              />
            </>
          )}
          <Input
            className='h-[40px] self-end'
            placeholder={t('eleo-tool-gallery-search-placeholder', 'Search')}
            onChange={setGallerySearch}
            value={gallerySearch}
          />
        </div>
      </div>
      <div className='flex h-full'>
        {/* Selector */}
        <div
          className={classNames(
            'bg-brand-form-accent-light relative flex w-full flex-col sm:w-[200px] md:w-[300px]',
            !isShowSelector && 'hidden sm:flex'
          )}
        >
          <div className='max-h-full overflow-y-auto p-[14px] pb-0'>
            {galleries.data.map((gallery) => (
              <ListPosition
                key={gallery.value}
                padding={12}
                height={width > 640 ? 35 : 40}
                text={gallery.label ?? <T keyName='eleo-default-gallery-name'>Default</T>}
                className={classNames(
                  'max-w-full truncate',
                  gallery.value === selectedGallery?.value
                    ? 'bg-brand-violet hover:bg-brand-violet !text-white'
                    : 'even:bg-brand-form-accent/05'
                )}
                onClick={() => {
                  if (gallery.value === selectedGallery?.value) return
                  setGalleryItems({ isLoading: true, data: [] })
                  setSelectedGallery(gallery)
                  setIsShowSelector(false)
                }}
              />
            ))}
          </div>

          <div className='mt-auto p-2 pt-0 sm:mt-0 sm:p-[14px]'>
            {Boolean(galleries.data.length) && (
              <div className='border-brand-form-accent-light mx-3 my-[6px] border-b' />
            )}
            <ListPosition
              text={<T keyName='eleo-create-gallery'>Create gallery</T>}
              icon={width > 640 ? <Add fontSize='inherit' /> : <AddCircle fontSize='inherit' />}
              iconPlacement='left'
              height={width > 640 ? 32 : 40}
              className='bg-brand-violet sm:text-brand-violet uppercase text-white sm:bg-transparent sm:normal-case'
              onClick={() =>
                setModalData({
                  isVisible: true,
                  title: (
                    <T
                      keyName='eleo-your-gallery-create-modal-title'
                      defaultValue='Create new gallery'
                    />
                  ),
                  acceptLabel: t('eleo-create', 'create'),
                  type: 'create',
                })
              }
            />
          </div>
        </div>

        {galleryItems.isLoading && !filteredItems?.length ? (
          <div className='flex h-full min-w-0 flex-1 items-center justify-center'>
            <CircularProgress className='!text-brand-violet-light' />
          </div>
        ) : (
          <GalleryList
            items={filteredItems}
            refreshData={fetchItems}
            setIsShowSelector={setIsShowSelector}
            setSelectedGallery={setSelectedGallery}
            selectedGallery={selectedGallery}
            setModalData={setModalData}
            setNewName={setNewName}
            handleDeleteGallery={handleDeleteGallery}
          />
        )}
      </div>

      <Modal
        isVisible={modalData.isVisible}
        containerClasses='bg-black bg-opacity-[3%]'
        hideModal={() => setModalData(DEFAULT_MODAL_DATA)}
        title={modalData.title}
        subtitle={modalData.subtitle}
        acceptLabel={modalData.acceptLabel}
        content={modalContent[modalData.type]}
        callback={(isSubmitSecondaryEffect) =>
          handleCallback(modalData.type, modalData.context, isSubmitSecondaryEffect)
        }
        isValid={
          !['create', 'rename', 'move-image'].includes(modalData.type) ||
          newName?.length ||
          newGallery
        }
        useSecondaryAccept={modalData.useSecondaryAccept}
        secondaryAcceptLabel={modalData.secondaryAcceptLabel}
        width='!'
        submitOnEnter={false}
      />
    </div>
  )
}
